import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { Search, X } from "lucide-react"
import * as React from "react"
import { PageHeader } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { QuickFilterToggle } from "@/features/offers-admin/QuickFilterToggle"
import { UsersAdminGrid } from "@/features/users-admin/UsersAdminGrid"
import type { UsersAdminInput } from "@/features/users-admin/users-admin-datasource"
import { useDebounce } from "@/hooks/use-debounce"
import type { AdminUserFiltersInput } from "@/types/marketplace.types"

export const Route = createFileRoute("/users/")({
	component: UsersPage,
	validateSearch: (search: Record<string, unknown>) => ({
		page: normalizePage(search.page),
		search: search.search as string | undefined,
		// Column-managed filters (CSV-encoded)
		isSeller: search.isSeller as string | undefined,
		accountStatus: search.accountStatus as string | undefined,
		sort: (search.sort as string) || undefined,
	}),
})

function normalizePage(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) return undefined
	const p = Math.floor(n)
	return p > 0 ? p : undefined
}

function DebouncedSearchInput({
	defaultValue,
	onDebouncedChange,
	inputRef,
}: {
	defaultValue: string
	onDebouncedChange: (value: string) => void
	inputRef?: React.RefObject<{ clear: () => void } | null>
}) {
	const [value, setValue] = React.useState(defaultValue)
	const debouncedValue = useDebounce(value, 150)
	const isInitialMount = React.useRef(true)

	React.useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false
			return
		}
		onDebouncedChange(debouncedValue)
	}, [debouncedValue, onDebouncedChange])

	React.useImperativeHandle(inputRef, () => ({
		clear: () => setValue(""),
	}))

	return (
		<div className="relative w-[260px] shrink-0">
			<Search className="absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-meta" />
			<Input
				placeholder="Id, username or email"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="pl-8"
			/>
		</div>
	)
}

function UsersPage() {
	const searchParams = useSearch({ from: "/users/" })
	const navigate = useNavigate()

	const pageIndex = (searchParams.page ?? 1) - 1
	const isSellerFilter = searchParams.isSeller ?? ""
	const accountStatusFilter = searchParams.accountStatus ?? ""

	const searchRef = React.useRef<{ clear: () => void }>(null)

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/users",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) => {
			updateFilters({ search: value || undefined, page: undefined })
		},
		[updateFilters],
	)

	const { sortBy, sortOrder } = React.useMemo(() => {
		if (searchParams.sort) {
			const [id, order] = searchParams.sort.split(":")
			return {
				sortBy: (id || "createdAt") as AdminUserFiltersInput["sortBy"],
				sortOrder: (order || "desc") as AdminUserFiltersInput["sortOrder"],
			}
		}
		return {
			sortBy: "createdAt" as const,
			sortOrder: "desc" as const,
		}
	}, [searchParams.sort])

	const gridInput = React.useMemo<UsersAdminInput>(() => {
		const input: UsersAdminInput = {
			sortBy,
			sortOrder,
		}
		if (searchParams.search) input.search = searchParams.search
		return input
	}, [searchParams.search, sortBy, sortOrder])

	const handlePageChange = React.useCallback(
		(nextPageIndex: number) => {
			updateFilters({
				page: nextPageIndex > 0 ? nextPageIndex + 1 : undefined,
			})
		},
		[updateFilters],
	)

	const handleSortChange = React.useCallback(
		(
			nextSortBy: AdminUserFiltersInput["sortBy"],
			nextSortOrder: AdminUserFiltersInput["sortOrder"],
		) => {
			const isDefault = nextSortBy === "createdAt" && nextSortOrder === "desc"
			updateFilters({
				sort: isDefault ? undefined : `${nextSortBy}:${nextSortOrder}`,
				page: undefined,
			})
		},
		[updateFilters],
	)

	const clearFilters = React.useCallback(() => {
		searchRef.current?.clear()
		navigate({ to: "/users", search: {} as never, replace: true })
	}, [navigate])

	const hasActiveFilters =
		!!searchParams.isSeller ||
		!!searchParams.accountStatus ||
		!!searchParams.search

	const toggleChip = React.useCallback(
		(key: "isSeller" | "accountStatus", value: string) => {
			const current = searchParams[key]
			updateFilters({
				[key]: current === value ? undefined : value,
				page: undefined,
			})
		},
		[searchParams, updateFilters],
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader title="Users" />

			<div className="min-h-0 flex-1">
				<UsersAdminGrid
					input={gridInput}
					pageIndex={pageIndex}
					onPageChange={handlePageChange}
					onSortChange={handleSortChange}
					toolbar={
						<>
							<DebouncedSearchInput
								defaultValue={searchParams.search ?? ""}
								onDebouncedChange={handleDebouncedSearch}
								inputRef={searchRef}
							/>

							<QuickFilterToggle
								active={isSellerFilter === "yes"}
								onToggle={() => toggleChip("isSeller", "yes")}
							>
								Sellers only
							</QuickFilterToggle>
							<QuickFilterToggle
								active={accountStatusFilter === "suspended"}
								onToggle={() => toggleChip("accountStatus", "suspended")}
							>
								Suspended
							</QuickFilterToggle>
							<QuickFilterToggle
								active={accountStatusFilter === "banned"}
								onToggle={() => toggleChip("accountStatus", "banned")}
							>
								Banned
							</QuickFilterToggle>
							<QuickFilterToggle
								active={accountStatusFilter === "flagged"}
								onToggle={() => toggleChip("accountStatus", "flagged")}
							>
								Flagged
							</QuickFilterToggle>

							{hasActiveFilters && (
								<Button variant="ghost" onClick={clearFilters}>
									<X />
									Clear
								</Button>
							)}
						</>
					}
				/>
			</div>
		</div>
	)
}
